import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { api } from '../services/api';

type Props = { deviceId: string };

function chip(label: string, ok: boolean | null, detail?: string) {
  const color = ok === true ? 'success' : ok === false ? 'error' : 'default';
  const variant = ok === null ? 'outlined' : 'filled';
  return <Chip size="small" label={detail ? `${label}: ${detail}` : label} color={color as any} variant={variant as any} />;
}

export const OcrPreflightPanel: React.FC<Props> = ({ deviceId }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setErr(null);
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/health`);
      setData(res.data || {});
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load edge health');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const ocrIdentity = data?.ocr_identity || null;
  const identityOk = typeof ocrIdentity?.ok === 'boolean' ? Boolean(ocrIdentity.ok) : null;
  const identityDetail =
    identityOk === true
      ? 'ok'
      : Array.isArray(ocrIdentity?.errors) && ocrIdentity.errors.length
        ? String(ocrIdentity.errors[0])
        : identityOk === false
          ? 'invalid'
          : 'unknown';

  const cameras = Array.isArray(data?.ocr_camera_health?.cameras) ? data.ocr_camera_health.cameras : [];
  const aliveCount = cameras.filter((c: any) => c?.alive === true).length;
  const camOk = cameras.length ? aliveCount > 0 : null;

  const disk = data?.disk_pressure || null;
  const diskOk = typeof disk?.ok === 'boolean' ? Boolean(disk.ok) : null;

  const outbox = data?.ocr_outbox || null;
  const outboxOk = typeof outbox?.ok === 'boolean' ? Boolean(outbox.ok) : null;

  const reasons = useMemo(() => {
    const rc = Array.isArray(data?.reason_codes) ? data.reason_codes : [];
    return rc.map((x: any) => String(x)).filter(Boolean);
  }, [data]);

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          OCR Preflight (v0)
        </Typography>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        )}
      </Stack>

      {err && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {err}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Minimal checks for early bring-up. Source: edge <code>/health</code> via cloud proxy.
      </Typography>

      <Divider sx={{ mb: 1 }} />

      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {chip('identity', identityOk, identityDetail)}
          {chip('cameras', camOk, cameras.length ? `${aliveCount}/${cameras.length} alive` : 'n/a')}
          {chip('disk', diskOk)}
          {chip('outbox', outboxOk)}
        </Stack>
      </Box>

      {reasons.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Degraded reasons: <strong>{reasons.join(', ')}</strong>
        </Typography>
      )}
    </Paper>
  );
};


